import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { motion, Variants } from 'framer-motion';

import { PATH_ROUTES } from 'routes/path';
import useAuthStore from 'store/auth';
import { Button } from '../ui/button';
import { MILESTONES } from 'lib/constants';
import { cn } from 'lib/utils';
import SettingsSheet from './SettingsSheet';
import useSidebar from 'hooks/useSidebar';
import { NavButton } from 'components/NavButton';
import Spinner from 'components/Spinner';
import useActiveNav from 'hooks/useActiveNav';
import useLogout from 'hooks/useLogout';

import MedsiLogo from 'assets/medsi-full.png';
import MedsiFullLogo from 'assets/medsi-check-full.png';

const sidebarVariants: Variants = {
  closed: {
    width: '5rem',
    transition: { type: 'spring', damping: 15, duration: 0.1 },
  },
  open: {
    width: '16rem',
    transition: { type: 'spring', damping: 15, duration: 0.1 },
  },
};
const toggleVariants: Variants = {
  closed: {
    left: '4rem',
    transition: { type: 'spring', duration: 0.1 },
  },
  open: {
    left: '15rem',
    transition: { type: 'spring', duration: 0.1 },
  },
};

export default function Sidebar({
  isExpanded,
  onExpandToggle,
}: {
  isExpanded: boolean;
  onExpandToggle: () => void;
}) {
  const navigate = useNavigate();
  const currentMilestone = useAuthStore((store) => store.currentMilestone);
  const accountStatus = useAuthStore((store) => store.accountStatus);
  const { upperNav, lowerNav } = useSidebar();
  const { activeNav } = useActiveNav();
  const { onLogout, isLoggingOut } = useLogout();

  const [openSettingSheet, setOpenSettingSheet] = useState<boolean>(false);

  const onSettingclick = () => setOpenSettingSheet(true);

  const UPPER_NAV = upperNav();
  const LOWER_NAV = lowerNav(onLogout, onSettingclick);

  const disabled = useMemo(
    () =>
      [MILESTONES.TNC, MILESTONES.USER_DETAIL].includes(
        currentMilestone as MILESTONES
      ),
    [currentMilestone]
  );

  return (
    <>
      <Spinner isSpinning={isLoggingOut} />

      <motion.div
        className='absolute z-50 hidden transition-all duration-200 lg:block top-4'
        variants={toggleVariants}
        initial='closed'
        animate={isExpanded ? 'open' : 'closed'}
      >
        <Button
          variant='ghost'
          className='px-0 border rounded-full shadow-md border-primary bg-primary size-8 hover:bg-primary'
          onClick={onExpandToggle}
          aria-label={isExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
        >
          {isExpanded ? (
            <ChevronsLeft className='text-white size-4' />
          ) : (
            <ChevronsRight className='text-white size-4' />
          )}
        </Button>
      </motion.div>

      <motion.aside
        variants={sidebarVariants}
        animate={isExpanded ? 'open' : 'closed'}
        initial='closed'
        className='absolute top-0 left-0 z-10 flex-col items-center justify-between hidden w-20 h-screen gap-10 p-5 overflow-x-hidden overflow-y-auto text-white shadow lg:flex bg-primary-dark'
      >
        <div className='space-y-4'>
          <Link
            to={PATH_ROUTES.DASHBOARD}
            className={cn('block', isExpanded ? 'h-6 w-full' : 'h-12 w-full')}
          >
            <img
              src={isExpanded ? MedsiFullLogo : MedsiLogo}
              alt='medsi logo'
              className='object-contain w-full h-full'
            />
          </Link>

          <div
            className={cn(
              'flex flex-col items-center justify-center space-y-1',
              isExpanded && 'w-full'
            )}
          >
            {UPPER_NAV.map(({ name, icon, href, size }) => (
              <NavButton
                icon={icon}
                name={name}
                disabled={
                  disabled ||
                  (href !== PATH_ROUTES.DASHBOARD && !accountStatus?.approved)
                }
                size={size}
                isActive={activeNav === name}
                onClick={() => {
                  if (href) navigate(href);
                }}
                isExpanded={isExpanded}
                key={icon}
              />
            ))}
          </div>
        </div>

        <div
          className={cn(
            'flex flex-col items-center justify-center space-y-1',
            isExpanded && 'w-full'
          )}
        >
          {LOWER_NAV.map(({ name, icon, href, onClick, size }) => {
            return (
              <NavButton
                icon={icon}
                name={name}
                disabled={
                  (disabled && icon !== 'logout') ||
                  (!accountStatus?.approved && icon === 'settings')
                }
                isActive={activeNav === name}
                onClick={() => {
                  if (href) navigate(href);
                  if (onClick) onClick();
                }}
                isExpanded={isExpanded}
                key={icon}
                size={size}
              />
            );
          })}
        </div>
      </motion.aside>

      <SettingsSheet
        open={openSettingSheet}
        onClose={() => setOpenSettingSheet(false)}
      />
    </>
  );
}
